/**
 * Browser push on this browser: what to say and when to ask (PUSH-ASK-1, BL-e113d148).
 *
 * The browser's "Allow notifications?" question is asked only when the person clicks "Turn on notifications on
 * this browser" in Settings. On load, an existing subscription is re-synced only when permission is already
 * granted, so a page load never asks. Kept free of the API client so the server tests can load it
 * (server/test/push-settings.test.ts).
 */

/** Where this browser stands. */
export type BrowserPushState = 'unsupported' | 'blocked' | 'not-configured' | 'off' | 'on';

/** What a browser reports, read by services/pushSubscription.ts. `permission` is null when there is no Notification API. */
export interface BrowserPushFacts {
  supported: boolean;
  permission: NotificationPermission | null;
  /** The server has VAPID keys (GET /api/notifications/vapid-key answered 200 with a key). */
  configured: boolean;
  /** This browser already holds a push subscription. */
  subscribed: boolean;
}

/** One state from the facts. The first that applies wins: a browser that cannot, then one that refuses, then the server. */
export function browserPushState(facts: BrowserPushFacts): BrowserPushState {
  if (!facts.supported || facts.permission === null) return 'unsupported';
  if (facts.permission === 'denied') return 'blocked';
  if (!facts.configured) return 'not-configured';
  return facts.permission === 'granted' && facts.subscribed ? 'on' : 'off';
}

/** The plain status line for each state. */
export const BROWSER_PUSH_STATUS_TEXT: Record<BrowserPushState, string> = {
  on: 'Notifications are on for this browser.',
  off: 'Notifications are off for this browser.',
  blocked: "Notifications are blocked for this site in this browser. To turn them on, allow notifications for this site in the browser's settings, then reload this page.",
  unsupported: 'This browser cannot show notifications from the portal.',
  'not-configured': 'Notifications are not set up on the server yet. An Admin sets them up with the keys below.',
};

/** The one action, shown only when it can work. */
export const TURN_ON_BUTTON_LABEL = 'Turn on notifications on this browser';

/** True when the state offers the turn-on button. */
export function canTurnOn(state: BrowserPushState): boolean {
  return state === 'off';
}

/** On load: re-sync the subscription only when the browser already allows notifications, so loading never asks. */
export function shouldResyncOnLoad(permission: NotificationPermission | null): boolean {
  return permission === 'granted';
}

/** The alert after a turn-on that did not end with notifications on; null when there is nothing to say. */
export function turnOnFailureText(outcome: string): string | null {
  switch (outcome) {
    case 'subscribed':
    case 'already-subscribed':
    case 'blocked':
      return null; // on, or the status line already says blocked
    case 'declined':
      return 'Notifications were not turned on because the browser request was closed without allowing them.';
    case 'not-configured':
      return BROWSER_PUSH_STATUS_TEXT['not-configured'];
    case 'unsupported':
      return BROWSER_PUSH_STATUS_TEXT.unsupported;
    default:
      return 'Notifications could not be turned on for this browser. Try again in a minute.';
  }
}
